import { useCallback, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Check } from '@mui/icons-material';
import { useAuth } from '@/shared/auth';
import { compareTiers, useGetSubscriptionPlans } from '@/entities/subscription';
import type { SubscriptionFeature, SubscriptionPlan, SubscriptionTier } from '@/entities/subscription';
import {
  BillingIntervalToggle,
  UpgradePlanCard,
  getUpgradeFeatures,
  useCreateCheckout,
  useUpgradeSubscription,
  type BillingInterval,
} from '@/features/subscription';
import { toError } from '@/shared/lib/toError';
import { useToast } from '@/shared/ui/toast';

interface FeatureUpgradePromptProps {
  requiredTier: SubscriptionTier;
  featureKey: SubscriptionFeature;
}

type UpgradableTier = 'pro' | 'elite';

const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  elite: 'Elite',
};

const CTA_LABEL_KEYS: Record<UpgradableTier, string> = {
  pro: 'subscription.upgrade.upgradeToPro',
  elite: 'subscription.upgrade.upgradeToElite',
};

const AUDIENCE_TAGLINE_KEYS: Record<UpgradableTier, string> = {
  pro: 'subscription.upgrade.forGrowingShops',
  elite: 'subscription.upgrade.forMultiLocation',
};

export const FeatureUpgradePrompt = ({ requiredTier, featureKey }: FeatureUpgradePromptProps) => {
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const { data: plansData, isLoading } = useGetSubscriptionPlans();
  const { toast } = useToast();
  const [interval, setInterval] = useState<BillingInterval>('year');

  const checkoutMutation = useCreateCheckout();
  const upgradeMutation = useUpgradeSubscription();

  const currentTier = sessionUser?.subscription.tier ?? 'free';

  const handleUpgradeError = useCallback(
    (error: Error) => {
      toast({
        title: t('common.error'),
        description: t('subscription.checkoutError'),
        variant: 'destructive',
      });
      console.error('Upgrade error:', error);
    },
    [t, toast]
  );

  const handleUpgrade = useCallback(
    (tier: UpgradableTier) => {
      if (currentTier === 'free') {
        checkoutMutation.mutate(
          { data: { tier, interval } },
          {
            onSuccess: (data) => {
              window.location.href = data.checkoutUrl;
            },
            onError: (error) => {
              handleUpgradeError(toError(error));
            },
          }
        );
        return;
      }

      if (currentTier === 'pro' && tier === 'elite') {
        upgradeMutation.mutate(
          { data: { targetTier: 'elite', interval } },
          {
            onError: (error) => {
              handleUpgradeError(toError(error));
            },
          }
        );
      }
    },
    [checkoutMutation, currentTier, handleUpgradeError, interval, upgradeMutation]
  );

  const handleProUpgrade = useCallback(() => {
    handleUpgrade('pro');
  }, [handleUpgrade]);

  const handleEliteUpgrade = useCallback(() => {
    handleUpgrade('elite');
  }, [handleUpgrade]);

  const qualifyingPlans: SubscriptionPlan[] =
    plansData?.plans?.filter(
      (plan): plan is SubscriptionPlan =>
        plan.tier !== 'free' && compareTiers(plan.tier, requiredTier) >= 0 && compareTiers(plan.tier, currentTier) > 0
    ) ?? [];

  const featureKeys = t(`subscription.featureGate.${featureKey}.features`, { returnObjects: true });
  const features: string[] = Array.isArray(featureKeys) ? featureKeys : [];

  const previousTierLabel = currentTier === 'free' ? TIER_LABELS.free : TIER_LABELS.pro;
  const variant = currentTier === 'free' ? 'from-free' : 'from-pro';
  const upgradeLoading = checkoutMutation.isPending || upgradeMutation.isPending;

  const upgradeHandlers: Record<UpgradableTier, () => void> = {
    pro: handleProUpgrade,
    elite: handleEliteUpgrade,
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          borderBottom: 1,
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
          }}
        >
          {t(`subscription.featureGate.${featureKey}.title`)}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }}>
          {t(`subscription.featureGate.${featureKey}.subtitle`)}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1200,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Feature highlights */}
        {features.length > 0 && (
          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                fontSize: { xs: '1.125rem', sm: '1.5rem' },
              }}
            >
              {t(`subscription.featureGate.${featureKey}.highlightsTitle`)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 1,
                px: { xs: 1, sm: 0 },
              }}
            >
              {features.map((feature) => (
                <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Check sx={{ width: 16, height: 16, color: 'success.main', flexShrink: 0 }} />
                  <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Plan cards */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && qualifyingPlans.length > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <BillingIntervalToggle value={interval} onChange={setInterval} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
              {qualifyingPlans.map((plan) => {
                const targetTier: UpgradableTier = plan.tier === 'pro' ? 'pro' : 'elite';
                return (
                  <Box key={plan.tier} sx={{ flex: '1 1 320px', maxWidth: 480 }}>
                    <UpgradePlanCard
                      plan={plan}
                      interval={interval}
                      previousTierLabel={previousTierLabel}
                      audienceTagline={t(AUDIENCE_TAGLINE_KEYS[targetTier])}
                      features={getUpgradeFeatures(targetTier)}
                      variant={variant}
                      ctaLabel={CTA_LABEL_KEYS[targetTier]}
                      onUpgrade={upgradeHandlers[targetTier]}
                      upgradeLoading={upgradeLoading}
                    />
                  </Box>
                );
              })}
            </Box>
          </>
        )}
        {!isLoading && qualifyingPlans.length === 0 && (
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {t('subscription.featureGate.noPlans')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
